import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import WorkspaceActivityViewer, { type ActivityOpenTarget } from './WorkspaceActivityViewer';
import { getClassroomActivityById, isActivityOpenable } from '../lib/classroomActivityRegistry';
import { inferViewerTypeFromExtension, toActivityEmbedUrl } from '../lib/activityEmbed';
import { masterAdminApi } from '../services/api';

export interface ModuleResourceLink {
  id: string;
  label: string;
  path: string;
}

export interface LearningModuleConfig {
  moduleId: string;
  title: string;
  journeyStage: string;
  coreContent: string;
  resourceLinks: ModuleResourceLink[];
  socraticGuardrails: string;
  updatedAt: string | null;
}

interface LearningModuleEditorProps {
  moduleId: string;
  onBack: () => void;
  onSaved?: () => void;
}

function createResourceLinkFromPick(input: {
  fileName: string;
  path: string;
  label?: string;
}, index: number): ModuleResourceLink {
  return {
    id: `res-new-${Date.now()}-${index}`,
    label: input.label?.trim() || input.fileName.replace(/\.[^.]+$/, '') || input.fileName,
    path: input.path,
  };
}

function isUserAddedResourceLink(link: ModuleResourceLink): boolean {
  return link.id.startsWith('res-new-');
}

function buildUserResourcePreviewTarget(link: ModuleResourceLink): ActivityOpenTarget | null {
  const filePath = link.path?.trim();
  if (!filePath) {
    return null;
  }

  if (filePath.startsWith('http')) {
    return {
      activityId: link.id,
      title: link.label || 'Linked document',
      kind: 'reading',
      viewerType: 'iframe',
      src: toActivityEmbedUrl(filePath),
      external: true,
      delivery: 'embed',
    };
  }

  const fileName = filePath.split(/[/\\]/).pop();
  if (!fileName) {
    return null;
  }

  const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
  return {
    activityId: link.id,
    title: link.label || fileName.replace(/\.[^.]+$/, ''),
    kind: 'media',
    viewerType: inferViewerTypeFromExtension(extension),
    src: `/api/master-admin/learning-modules/activities-vault/file?fileName=${encodeURIComponent(fileName)}`,
    external: false,
    delivery: 'vault',
    fileName,
  };
}

function buildClientPreviewTarget(link: ModuleResourceLink): ActivityOpenTarget | null {
  if (isUserAddedResourceLink(link)) {
    return buildUserResourcePreviewTarget(link);
  }

  const asset = getClassroomActivityById(link.id);
  if (!asset || !isActivityOpenable(asset)) {
    return null;
  }

  const embedUrl = link.path?.trim() || asset.url;
  if (embedUrl?.startsWith('http')) {
    return {
      activityId: asset.id,
      title: link.label || asset.title,
      kind: asset.kind,
      viewerType: 'iframe',
      src: toActivityEmbedUrl(embedUrl),
      external: true,
      delivery: asset.delivery,
    };
  }

  if (asset.extension) {
    return {
      activityId: asset.id,
      title: link.label || asset.title,
      kind: asset.kind,
      viewerType: inferViewerTypeFromExtension(asset.extension),
      src: `/api/master-admin/activities/${encodeURIComponent(asset.id)}/file`,
      external: false,
      delivery: asset.delivery,
      fileName: `${asset.fileStem}.${asset.extension}`,
    };
  }

  return null;
}

export default function LearningModuleEditor({ moduleId, onBack, onSaved }: LearningModuleEditorProps) {
  const [config, setConfig] = useState<LearningModuleConfig | null>(null);
  const [coreContent, setCoreContent] = useState('');
  const [resourceLinks, setResourceLinks] = useState<ModuleResourceLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [previewTarget, setPreviewTarget] = useState<ActivityOpenTarget | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewActivityId, setPreviewActivityId] = useState<string | null>(null);
  const [previewWindowOpen, setPreviewWindowOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [vaultCloudPath, setVaultCloudPath] = useState('');
  const [vaultBusy, setVaultBusy] = useState(false);

  useEffect(() => {
    masterAdminApi.getActivitiesVaultInfo()
      .then((info) => {
        setVaultCloudPath(String(info.cloudPath ?? ''));
      })
      .catch(() => {
        // Vault path label is optional until the user adds a document.
      });
  }, [moduleId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaveNotice(null);
    setPreviewTarget(null);
    setPreviewError(null);
    setPreviewActivityId(null);
    setPreviewWindowOpen(false);
    setPreviewTitle('');

    masterAdminApi.getLearningModuleConfig(moduleId)
      .then((data) => {
        if (cancelled) return;
        setConfig(data.config);
        setCoreContent(data.config.coreContent || '');
        setResourceLinks(data.config.resourceLinks || []);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load module configuration.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  const removeResourceLink = (id: string) => {
    setResourceLinks((links) => links.filter((link) => link.id !== id));
    if (previewActivityId === id) {
      closePreview();
    }
  };

  const handleAddDocument = async () => {
    setVaultBusy(true);
    setError(null);

    try {
      const result = await masterAdminApi.pickActivitiesVaultFile() as {
        cancelled?: boolean;
        error?: string;
        fileName?: string;
        path?: string;
        label?: string;
        cloudPath?: string;
      };

      if (result.cancelled) {
        return;
      }

      if (result.cloudPath) {
        setVaultCloudPath(result.cloudPath);
      }

      if (result.error || !result.path || !result.fileName) {
        if (result.error === 'outside_vault') {
          setError('Choose a file inside the Activities vault folder on M:.');
        } else if (result.error === 'file_not_found') {
          setError('That file could not be found. Choose a file from the Activities vault.');
        } else {
          setError('Unable to open the Activities vault file picker. Run npm run dev on this PC and confirm the M: drive is mounted.');
        }
        return;
      }

      setResourceLinks((links) => [
        ...links,
        createResourceLinkFromPick(
          {
            fileName: result.fileName,
            path: result.path,
            label: result.label,
          },
          links.length + 1,
        ),
      ]);
    } catch {
      setError('Unable to open the Activities vault file picker. Run npm run dev on this PC and confirm the M: drive is mounted.');
    } finally {
      setVaultBusy(false);
    }
  };

  const closePreview = () => {
    setPreviewTarget(null);
    setPreviewActivityId(null);
    setPreviewError(null);
    setPreviewWindowOpen(false);
    setPreviewTitle('');
    setPreviewLoading(false);
  };

  useEffect(() => {
    if (!previewWindowOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePreview();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [previewWindowOpen]);

  const openResourcePreview = async (link: ModuleResourceLink) => {
    setPreviewWindowOpen(true);
    setPreviewTitle(link.label || 'Student preview');
    setPreviewActivityId(link.id);

    if (isUserAddedResourceLink(link)) {
      const userTarget = buildUserResourcePreviewTarget(link);
      if (userTarget) {
        setPreviewTarget(userTarget);
        setPreviewTitle(userTarget.title || link.label);
        setPreviewLoading(false);
        return;
      }

      setPreviewError('This document is not available yet. Pick a file from the Activities vault and save.');
      setPreviewTarget(null);
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewTarget(null);

    try {
      const data = await masterAdminApi.openActivityPreview(link.id);
      const target = (data as { target?: ActivityOpenTarget }).target;
      if (!target) {
        throw new Error('missing_target');
      }
      setPreviewTarget(target);
      setPreviewTitle(target.title || link.label);
    } catch {
      const fallback = buildClientPreviewTarget(link);
      if (fallback) {
        setPreviewTarget(fallback);
        setPreviewTitle(fallback.title || link.label);
      } else {
        setPreviewTarget(null);
        setPreviewError('Student preview is not available for this document yet.');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveNotice(null);

    try {
      const result = await masterAdminApi.updateLearningModuleConfig(moduleId, {
        coreContent,
        resourceLinks,
      });
      setConfig(result.config);
      setCoreContent(result.config.coreContent || '');
      setResourceLinks(result.config.resourceLinks || []);
      setSaveNotice('Module configuration saved to SQLite storage.');
      onSaved?.();
    } catch {
      setError('Save failed. Check server connectivity and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="org-panel-card socratic-content-lab">
        <p className="socratic-lab-loading">Loading Socratic Content Lab…</p>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="org-panel-card socratic-content-lab">
        <p className="socratic-lab-error">{error}</p>
        <button type="button" className="socratic-lab-back-btn" onClick={onBack}>
          BACK TO MATRIX
        </button>
      </div>
    );
  }

  return (
    <div className="org-panel-card socratic-content-lab">
      <header className="socratic-lab-header">
        <div>
          <p className="socratic-lab-kicker">Socratic Content Lab</p>
          <h2 className="section-header socratic-lab-title">
            Module {config?.moduleId}: {config?.title}
          </h2>
          <p className="socratic-lab-subtitle">
            Journey Stage: {config?.journeyStage || '—'} · Source: Mentology Classroom curriculum (Rev 2)
          </p>
        </div>
        <div className="socratic-lab-header-actions">
          <button type="button" className="socratic-lab-back-btn" onClick={onBack}>
            BACK TO MATRIX
          </button>
          <button
            type="button"
            className="socratic-lab-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'SAVING…' : 'SAVE CHANGES'}
          </button>
        </div>
      </header>

      {error ? <p className="socratic-lab-error">{error}</p> : null}
      {saveNotice ? <p className="socratic-lab-notice socratic-lab-status-line">{saveNotice}</p> : null}

      <div className="socratic-lab-body">
        <section className="socratic-lab-section socratic-lab-section-core">
          <label className="socratic-lab-label" htmlFor={`core-content-${moduleId}`}>
            Text Area 1 — Core Content
          </label>
          <p className="socratic-lab-help">
            Main module curriculum text injected into the active module anchor.
          </p>
          <textarea
            id={`core-content-${moduleId}`}
            className="socratic-lab-textarea socratic-lab-textarea-core"
            value={coreContent}
            onChange={(event) => setCoreContent(event.target.value)}
            spellCheck
          />
        </section>

        <section className="socratic-lab-section socratic-lab-section-resources">
          <div className="socratic-lab-section-heading">
            <div>
              <h3 className="socratic-lab-label">Section 2 — Resource Linker</h3>
              <p className="socratic-lab-help">
                Click a reading or form to open the student preview in a full-screen window.
                Use [Add/Edit New Document] to pick a file from the Activities vault, then Save Changes.
              </p>
            </div>
            <button
              type="button"
              className="socratic-lab-add-btn"
              onClick={() => { void handleAddDocument(); }}
              disabled={vaultBusy}
            >
              {vaultBusy ? 'CHOOSE FILE…' : '[Add/Edit New Document]'}
            </button>
          </div>

          {vaultCloudPath ? (
            <p className="socratic-lab-vault-path">
              Activities vault: <code>{vaultCloudPath}</code>
            </p>
          ) : null}

          <div className="socratic-lab-resource-list">
            {resourceLinks.length === 0 ? (
              <p className="socratic-lab-empty">No documents linked yet. Use [Add/Edit New Document] to attach readings.</p>
            ) : (
              resourceLinks.map((link) => (
                <div
                  key={link.id}
                  className={`socratic-lab-resource-row${previewActivityId === link.id ? ' socratic-lab-resource-row-active' : ''}`}
                >
                  {isUserAddedResourceLink(link) ? (
                    <button
                      type="button"
                      className="socratic-lab-resource-open-btn"
                      onClick={() => { void openResourcePreview(link); }}
                      disabled={previewLoading && previewActivityId === link.id}
                    >
                      {link.label || 'Untitled document'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="socratic-lab-resource-open-btn"
                      onClick={() => { void openResourcePreview(link); }}
                      disabled={previewLoading && previewActivityId === link.id}
                    >
                      {link.label || 'Untitled document'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="socratic-lab-remove-btn"
                    onClick={() => removeResourceLink(link.id)}
                    aria-label={`Delete ${link.label || 'document'}`}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {previewWindowOpen && createPortal(
        <div
          className="socratic-lab-preview-window"
          role="dialog"
          aria-modal="true"
          aria-label={previewTitle || 'Student document preview'}
        >
          <header className="socratic-lab-preview-window-header">
            <div className="socratic-lab-preview-window-heading">
              <p className="socratic-lab-preview-window-kicker">Student Preview</p>
              <h3 className="socratic-lab-preview-window-title">{previewTitle}</h3>
            </div>
            <button
              type="button"
              className="socratic-lab-preview-window-back"
              onClick={closePreview}
            >
              Back to Learning Module
            </button>
          </header>

          <div className="socratic-lab-preview-window-body">
            {previewLoading ? (
              <p className="socratic-lab-preview-window-status">Loading student view…</p>
            ) : null}
            {previewError ? (
              <p className="socratic-lab-error socratic-lab-preview-window-status">{previewError}</p>
            ) : null}
            {previewTarget ? (
              <WorkspaceActivityViewer target={previewTarget} onBack={closePreview} />
            ) : null}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
